import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { RootState } from '../root/root-types';
import {
  getRefreshToken,
  getToken,
  getWorkspaceId,
  logOut,
  onRefreshToken,
} from '../user-auth/userAuth.slice';
import { getAppState } from '../app/app.slice';
import { APP_TYPE } from '../app/app.model';
import { UserTokens } from '../user-auth/userAuth.interface';
import { getConfigValue } from '@zonofi/common/functions/get-config-value/get-config-value';

const REDUCER_PATH_ZONO_API = 'zonoAPI';

const baseQuery = fetchBaseQuery({
  baseUrl: getConfigValue('API_ENDPOINT'),
  prepareHeaders: (headers, { getState }) => {
    const accessToken = getToken(getState() as RootState);
    if (!headers.has('authorization') && accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
      headers.set('Cache-Control', 'no-cache'); // 🚫 Prevent caching
    }
    return headers;
  },
});

export const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  any,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  const state = api.getState() as RootState;
  const refreshToken = getRefreshToken(state);
  const accessToken = getToken(state);
  const workspaceId = getWorkspaceId(state);
  const appState = getAppState(state);

  const shouldAttemptRefresh =
    (result.error?.status === 401 ||
      (result.error?.status === 'PARSING_ERROR' &&
        result.error?.originalStatus === 401)) &&
    !['sendOTP', 'verifyOTP'].includes(api.endpoint);

  if (shouldAttemptRefresh) {
    //using js redirect to /error-screen
    if (appState !== APP_TYPE.ZONO_MONEY) {
      if (!refreshToken) api.dispatch(logOut());
      const refreshResult = await baseQuery(
        {
          url: 'regenerate/token',
          method: 'POST',
          body: {
            refreshToken: refreshToken,
            token: accessToken,
            workspaceId: workspaceId,
          },
        },
        api,
        extraOptions
      );
      if (refreshResult.data) {
        const tokens = refreshResult.data as UserTokens;
        api.dispatch(onRefreshToken(tokens));
        result = await baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(logOut());
      }
    } else {
      window.location.href = '/error-screen';
    }
  }
  return result;
};

export const zonoApi = createApi({
  reducerPath: REDUCER_PATH_ZONO_API,
  baseQuery: baseQueryWithReAuth,
  endpoints: () => ({}),
  tagTypes: ['User', 'CustomerInvite', 'FiledOps', 'trip'],
});
